/**
 * groovi-live-price.js — ONE shared live-price reflector (Feature A / D5).
 *
 * A shared <product-price> MutationObserver that, on any price change (a Kaching
 * Bundles tier, a native pack-size variant), recomputes its consumers together:
 *   • the installments line (.groovi-installments / [data-groovi-installments]) — I1
 *   • the per-unit badge ([data-sl-unit]) — U1 live / U2
 *   • the PDP free-shipping progress (groovi-freeship[data-context="product"])
 * Read-only: it never manages Kaching or theme state, only observes the visible price
 * and rewrites text. Idempotent (writes only when the text changes → no observer loop).
 * Currency/locale/settings all come from data-* on the elements — nothing hardcoded.
 *
 * Money parse: the visible price text is read as minor units assuming 2 decimals
 * (BRL ✓). The installments formula mirrors snippets/installments.liquid exactly:
 *   display_count = min(count, floor(price/min)); per = floor(price/display_count).
 *
 * U2 — Quantity comes from the selected MAIN Kaching deal bar: its quantity
 * selector first, then the matching dealBars[].quantity configuration. Collection
 * breaks may leave the native form quantity at 1 even when 2/3 boxes are selected.
 * A scoped native quantity is only a legacy fallback for an actual selected bar.
 */
(function () {
  'use strict';

  // ── Dependency map (2026-07-04) ──────────────────────────────────────────────
  // This reflector is the shared runtime dependency of the server-rendered price features.
  // It only ever READS the live price and REWRITES existing elements — it never
  // creates them — so each feature degrades gracefully when its element is absent.
  //
  //   • UNIT_SEL  → the per-unit badge, `blocks/sl-unit.liquid` ("unidade - by
  //     studiolab"). As of 2026-07-04 this badge is also addable INSIDE product cards
  //     (whitelisted in `blocks/_product-card.liquid` + `_product-card-group.liquid`),
  //     so a collection/search page can hold MANY `[data-sl-unit]` nodes. The write is
  //     scoped to the main buy-box (inMainBuyBox) so card/cross-sell badges keep their
  //     correct server-rendered value. Units source: block setting → `custom.units`
  //     metafield (see Groovi_Metafields_Reference.md).
  //
  //   • INST_SEL  → the installments line, `snippets/installments.liquid`. As of
  //     2026-07-04 a product can opt OUT of installments entirely via the boolean
  //     metafield `custom.installments` (ON = hidden). When ON, the snippet renders
  //     NOTHING, so `[data-groovi-installments]` simply does not exist on the page and
  //     this reflector no-ops for it — no JS change was needed for the opt-out.
  // ─────────────────────────────────────────────────────────────────────────────
  var UNIT_SEL = '[data-sl-unit]';
  var INST_SEL = '[data-groovi-installments]';
  var SHIPPING_SEL = 'groovi-freeship[data-context="product"]';
  var PENDING_CLASS = 'sl-unit--pending';

  function parseMinor(text) {
    var digits = (text || '').replace(/\D/g, '');
    return digits ? parseInt(digits, 10) : NaN;
  }

  function formatMoney(major, locale, currency) {
    try {
      return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency: currency }).format(major);
    } catch (e) {
      return major.toFixed(2);
    }
  }

  // First actually-visible `.price` within a scope (skips the hidden regular/sale
  // branch + a11y-hidden copies).
  function visiblePrice(scope) {
    var nodes = scope.querySelectorAll('.price');
    for (var i = 0; i < nodes.length; i++) {
      var p = nodes[i];
      if (p.offsetParent !== null && !p.closest('.price__hidden, .visually-hidden, [hidden]')) return p;
    }
    return null;
  }

  // The live main-product total, in minor units, read from <product-price> (the element
  // Kaching/variants rewrite) — never the whole document, which may hold a second
  // static .price (a cross-sell/buy-box) that would win as the first visible match.
  function liveTotalMinor() {
    var pp = document.querySelector('.product-information product-price');
    if (!pp) return NaN;
    var el = visiblePrice(pp);
    return el ? parseMinor(el.textContent) : NaN;
  }

  function mainBundleBars() {
    return Array.prototype.filter.call(
      document.querySelectorAll('.product-information .kaching-bundles__bar'),
      inMainBuyBox
    );
  }

  function selectedBundleBar() {
    var bars = mainBundleBars();
    // The radio's property reflects the current selection, even without an HTML
    // checked attribute. The class supports app versions without native radios.
    for (var i = 0; i < bars.length; i++) {
      if (bars[i].querySelector('input[type="radio"]:checked')) return bars[i];
    }
    for (var j = 0; j < bars.length; j++) {
      if (bars[j].classList.contains('kaching-bundles__bar--selected')) return bars[j];
    }
    return null;
  }

  function positiveQuantity(value) {
    var count = Number(value);
    return isFinite(count) && count >= 1 && Math.floor(count) === count ? count : 0;
  }

  function kachingSelectedPacks() {
    var selected = selectedBundleBar();
    if (!selected) return 1; // No selection: use only the product's base units.

    var selector = selected.querySelector('.kaching-bundles__quantity-selector__input');
    var quantity = selector && positiveQuantity(selector.value);
    if (quantity) return quantity;

    // Public widget configuration, matched by ID — never parse translated labels
    // such as "2 Caixas" / "16 pacotinhos", and never count filled flavor slots.
    var widget = selected.closest('kaching-bundles-block');
    var id = selected.getAttribute('data-deal-bar-id');
    if (widget && id) {
      try {
        var deal = JSON.parse(widget.getAttribute('deal-block') || '{}');
        var bars = Array.isArray(deal.dealBars) ? deal.dealBars : [];
        for (var i = 0; i < bars.length; i++) {
          if (bars[i].id === id) {
            quantity = positiveQuantity(bars[i].quantity);
            if (quantity) return quantity;
          }
        }
      } catch (e) { /* Older app versions can still sync the native form below. */ }
    }

    var packs = 1;
    var inputs = document.querySelectorAll('.product-information input[name="quantity"]');
    for (var j = 0; j < inputs.length; j++) {
      if (!inMainBuyBox(inputs[j])) continue;
      packs = Math.max(packs, positiveQuantity(inputs[j].value));
    }
    return packs;
  }

  // Only the MAIN product's price consumers may be rewritten with the live main price.
  // The read is already scoped to <product-price>; the WRITE must be too, else the main
  // price gets stamped onto every OTHER product's server-rendered installments/unit line
  // (cross-sell, recommendations, related). I1/U2/D5 scope fix.
  //
  // ALLOWLIST (robust — the recommendation/related rails live OUTSIDE these, so any future
  // card type there is auto-excluded): the element must sit in the main PDP info column
  // (.product-information) or the sticky ATC bar (.sticky-add-to-cart).
  // MINUS nested cards: the "COMBINA COM" cross-sell (.groovi-crosssell-card) renders as a
  // block INSIDE .product-information, so it must be explicitly excluded; .resource-card /
  // .product-card guard any card variant that lands in the column too.
  function inMainBuyBox(el) {
    if (!el.closest('.product-information, .sticky-add-to-cart')) return false;
    if (el.closest('.groovi-crosssell-card, .resource-card, .product-card')) return false;
    return true;
  }

  function updateInstallments(totalMinor) {
    var els = document.querySelectorAll(INST_SEL);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!inMainBuyBox(el)) continue;
      var count = parseInt(el.dataset.instCount, 10) || 6;
      var min = parseInt(el.dataset.instMin, 10) || 0;
      var display = count;
      if (min > 0) {
        var byAmount = Math.floor(totalMinor / min);
        if (byAmount < count) display = byAmount;
      }
      if (display < 2) { el.hidden = true; continue; }
      var perMinor = Math.floor(totalMinor / display); // matches Liquid integer divided_by
      var per = formatMoney(perMinor / 100, el.dataset.locale, el.dataset.currency);
      var text = (el.dataset.instTpl || '').replace('__COUNT__', display).replace('__AMOUNT__', per);
      if (el.textContent !== text) el.textContent = text;
      el.hidden = false;
    }
  }

  // Scope the offer to the main PDP; a recommendation widget is not its offer.
  function bundleActive() {
    return mainBundleBars().length > 0;
  }

  function updateUnits(totalMinor) {
    var boxes = kachingSelectedPacks();
    var els = document.querySelectorAll(UNIT_SEL);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (!inMainBuyBox(el)) continue;
      var units = parseInt(el.dataset.unitsPerPack, 10) || 0; // custom.units — units in ONE box/pack
      if (units <= 0) continue;
      // Units in one product remain the Liquid/metafield value. Only a selected
      // Kaching offer multiplies them; ordinary form quantity never does.
      var perUnit = totalMinor / 100 / (boxes * units);
      var suffix = el.dataset.suffix || '';
      var text = formatMoney(perUnit, el.dataset.locale, el.dataset.currency) + (suffix ? ' ' + suffix : '');
      if (el.textContent !== text) el.textContent = text;
      el.classList.remove(PENDING_CLASS);
    }
  }

  function recompute() {
    var totalMinor = liveTotalMinor();
    if (!isFinite(totalMinor) || totalMinor < 0) return; // no live price → keep server values
    updateInstallments(totalMinor);
    updateUnits(totalMinor);
    // Kaching's visible price is already the discounted pack total. Plain PDPs
    // display a single variant price, so their quantity must be applied once.
    var quantity = document.querySelector('.product-information input[name="quantity"]');
    var count = bundleActive() ? 1 : Math.max(1, parseInt(quantity && quantity.value, 10) || 1);
    document.querySelectorAll(SHIPPING_SEL).forEach(function (bar) {
      if (!inMainBuyBox(bar)) return;
      var value = String(totalMinor * count);
      if (bar.dataset.productTotal !== value) bar.dataset.productTotal = value;
    });
  }

  function init() {
    // Bind even when all consumers are hidden, so adding the block in the editor works.
    if (!document.querySelector('.product-information')) return;

    var raf = null;
    var schedule = function () {
      if (raf) return; // coalesce bursts (idempotent writes already prevent loops)
      raf = requestAnimationFrame(function () { raf = null; recompute(); });
    };

    var observed = new WeakSet();
    function observePrices() {
      document.querySelectorAll('product-price').forEach(function (pp) {
        if (observed.has(pp)) return;
        observed.add(pp);
        new MutationObserver(schedule).observe(pp, { childList: true, characterData: true, subtree: true });
      });
    }
    var observedBuyBoxes = new WeakSet();
    function observeBundleSelections() {
      document.querySelectorAll('.product-information').forEach(function (root) {
        if (observedBuyBoxes.has(root)) return;
        observedBuyBoxes.add(root);
        new MutationObserver(function (records) {
          var changed = records.some(function (record) {
            var target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
            if (target && target.closest('kaching-bundles-block, kaching-bundle, .kaching-bundles') && inMainBuyBox(target)) return true;
            // Also catch asynchronous widget insertion/removal/replacement. Keep
            // observing the stable buy box rather than a disposable app subtree.
            return Array.prototype.some.call(record.addedNodes, isBundleNode) ||
              Array.prototype.some.call(record.removedNodes, isBundleNode);
          });
          if (changed) schedule();
        }).observe(root, {childList: true, characterData: true, subtree: true,
          attributes: true, attributeFilter: ['class', 'checked', 'value', 'deal-block']});
      });
    }
    function isBundleNode(node) {
      return node.nodeType === 1 && (node.matches('kaching-bundles-block, kaching-bundle, .kaching-bundles') ||
        !!node.querySelector('kaching-bundles-block, kaching-bundle, .kaching-bundles'));
    }
    observePrices();
    observeBundleSelections();
    // Flavor navigation and Theme Editor reloads can replace the entire buy box.
    document.addEventListener('shopify:section:load', function () {
      observePrices();
      observeBundleSelections();
      schedule();
    });

    // Belt-and-suspenders: Kaching may settle price + quantity a tick apart; re-run on
    // either. Delegated on document so it survives app re-renders.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && (t.name === 'quantity' || (t.name && t.name.indexOf('kaching') === 0))) schedule();
    });
    document.addEventListener('input', function (e) {
      if (e.target && (e.target.name === 'quantity' ||
        (e.target.matches('.kaching-bundles__quantity-selector__input') && inMainBuyBox(e.target)))) schedule();
    });

    recompute(); // correct any pre-selected tier / variant on load

    // Kaching renders asynchronously; its bar can paint AFTER the api global appears and
    // after it sets the default tier + quantity. Recompute until the bundle bar is actually
    // on the page (so the badge reflects the selected tier), or ~10s, then stop. Idempotent
    // writes make the extra passes free; a product with no bundle simply keeps the single-box
    // value computed from custom.units.
    var tries = 0;
    (function poll() {
      recompute();
      if (bundleActive()) return;
      if (tries++ < 40) setTimeout(poll, 250); // ~10s ceiling, then give up (no Kaching here)
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
